// Reading list — saved places and stories live in localStorage (no account
// needed): places as {id, name, country}, stories as {key, title, cat, url}.
// This module owns the storage, the modal, the count badge and the article
// page's "Save" button; the map module only asks it to save/open a place.
import { $, t, toast, storageGet, storageSet, data } from "./dom.js";

const PLACES_KEY = "sd-saved";
const STORIES_KEY = "sd-saved-stories";

export const readingList = {
  places: storageGet(PLACES_KEY, []).filter((p) => p && p.id),
  stories: storageGet(STORIES_KEY, []).filter((s) => s && s.key),

  hasPlace(id) {
    return this.places.some((p) => p.id === id);
  },
  hasStory(key) {
    return this.stories.some((s) => s.key === key);
  },
  togglePlace(place) {
    const i = this.places.findIndex((p) => p.id === place.id);
    if (i > -1) this.places.splice(i, 1);
    else this.places.push({ id: place.id, name: place.name, country: place.country });
    storageSet(PLACES_KEY, this.places);
    this.onChange();
    return i === -1;
  },
  toggleStory(story) {
    const i = this.stories.findIndex((s) => s.key === story.key);
    if (i > -1) this.stories.splice(i, 1);
    else this.stories.push(story);
    storageSet(STORIES_KEY, this.stories);
    this.onChange();
    return i === -1;
  },
  onChange() {
    const count = $(".js-rl-count");
    if (count) count.textContent = this.places.length + this.stories.length;
  },
};

const esc = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Set per page by script.js: the current page's map controller (or null).
export const pageState = { map: null };

// Modal wiring — runs once; the modal lives in the footer and outlives page swaps.
export function initReadingListModal() {
  const modal = $(".js-rl-modal");
  const list = $(".js-rl-list");
  if (!modal || !list) return;

  const render = () => {
    if (!readingList.places.length && !readingList.stories.length) {
      list.innerHTML = '<div class="rl-empty">' + esc(t("nothing")) + "<br>" + esc(data.i18n.emptyHint || "") + "</div>";
      return;
    }
    let html = "";
    if (readingList.stories.length) {
      html += '<div class="rl-group">' + esc(t("stories")) + "</div>";
      html += readingList.stories
        .map(
          (s) =>
            '<div class="rl-item"><div class="rl-item__meta"><div class="rl-item__name">' + esc(s.title) + '</div><div class="rl-item__sub">' + esc(s.cat) + "</div></div>" +
            '<a class="rl-item__open" href="' + esc(s.url) + '" data-rl-link>' + esc(t("read")) + "</a>" +
            '<button type="button" class="rl-item__rm" data-rm-story="' + esc(s.key) + '">' + esc(t("remove")) + "</button></div>"
        )
        .join("");
    }
    if (readingList.places.length) {
      html += '<div class="rl-group">' + esc(t("places")) + "</div>";
      html += readingList.places
        .map(
          (p) =>
            '<div class="rl-item"><div class="rl-item__meta"><div class="rl-item__name">' + esc(p.name) + '</div><div class="rl-item__sub">' + esc(p.country) + "</div></div>" +
            '<button type="button" class="rl-item__open" data-open-place="' + esc(p.id) + '">' + esc(t("open")) + "</button>" +
            '<button type="button" class="rl-item__rm" data-rm-place="' + esc(p.id) + '">' + esc(t("remove")) + "</button></div>"
        )
        .join("");
    }
    list.innerHTML = html;
  };

  const open = () => {
    render();
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
  };
  const close = () => {
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
  };

  // The open button lives in the (swapped) map section, so listen on document.
  document.addEventListener("click", (e) => {
    if (e.target.closest(".js-rl-open")) open();
    else if (e.target.closest(".js-rl-close")) close();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") close();
  });

  list.addEventListener("click", (e) => {
    const rmStory = e.target.closest("[data-rm-story]");
    const rmPlace = e.target.closest("[data-rm-place]");
    const openBtn = e.target.closest("[data-open-place]");
    if (e.target.closest("[data-rl-link]")) close();
    if (rmStory) {
      readingList.toggleStory({ key: rmStory.dataset.rmStory });
      render();
    } else if (rmPlace) {
      readingList.togglePlace({ id: rmPlace.dataset.rmPlace });
      render();
      if (pageState.map) pageState.map.refresh();
    } else if (openBtn) {
      close();
      if (pageState.map) pageState.map.byId(openBtn.dataset.openPlace);
      else window.location.href = data.homeUrl + "#place-" + encodeURIComponent(openBtn.dataset.openPlace);
    }
  });
}

// Per page: the count badge and the story page's "Save" button.
export function initReadingListPage() {
  readingList.onChange();
  initSaveStory();
}

// "Save" button on a story page.
function initSaveStory() {
  const button = $(".js-save-story");
  if (!button) return;
  const label = $(".js-save-label", button);

  const paint = () => {
    const on = readingList.hasStory(button.dataset.key);
    button.classList.toggle("is-saved", on);
    button.setAttribute("aria-pressed", on ? "true" : "false");
    if (label) label.textContent = on ? t("saved") : t("save");
  };
  paint();

  button.addEventListener("click", () => {
    const added = readingList.toggleStory({
      key: button.dataset.key,
      title: button.dataset.title,
      cat: button.dataset.cat,
      url: button.dataset.url,
    });
    toast(added ? t("savedToast") : t("removedToast"));
    paint();
  });
}
